import {
  Icon,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text
} from '@chakra-ui/react';
import React from 'react';

import type { DictionaryOption } from '../../interface/api';
import type { BillingLocationData } from '../../interface/billingLocation';
import { useCodingScreen } from '../../interface/context';
import type { SlotProps } from '../../interface/slotProps';
import {
  INK,
  INK_MUTED,
  POPOVER_BORDER,
  POPOVER_RADIUS,
  POPOVER_SHADOW,
  SURFACE_WARM
} from '../../styles/tokens';
import { FacilityEditForm, type FacilityEditStatus } from './FacilityEditForm';

const INTER_STACK = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

/** Inset of the edit affordance from the popover's top and right edges — the same value for both. */
const EDIT_AFFORDANCE_INSET = '8px';

/**
 * Space kept clear to the right of the read-only text so a long facility name passes behind the
 * edit affordance instead of under it.
 */
const EDIT_AFFORDANCE_GUTTER = '26px';

const HOVER_HINT_SX = {
  cursor: 'help',
  textDecoration: 'underline dotted var(--border-strong, #c9c8ce)',
  textUnderlineOffset: '3px',
  '&:hover, &:focus-visible': {
    textDecorationColor: 'var(--text-muted, #7a7682)'
  }
} as const;

const FACILITY_DICTIONARY = {
  dictionaryName: 'Facility',
  columnName: 'Facility'
} as const;

const LOAD_FAILED = "Couldn't load the facility list. Try again.";
const NO_OPTIONS = 'No facilities are available to choose from.';
const SAVE_FAILED = "Couldn't save the facility. Try again.";

const na = (v: unknown): string =>
  typeof v === 'string' || typeof v === 'number' ? String(v) : 'N/A';

export const BillingLocation: React.FC<SlotProps> = ({
  value,
  mode: slotMode
}) => {
  const { api } = useCodingScreen();
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLSpanElement>(null);
  const selectRef = React.useRef<HTMLSelectElement>(null);

  const data: BillingLocationData =
    value != null && typeof value === 'object' && !Array.isArray(value)
      ? (value as BillingLocationData)
      : {};

  const [mode, setMode] = React.useState<'view' | 'edit'>('view');
  const [isOpen, setIsOpen] = React.useState(false);
  const [status, setStatus] = React.useState<FacilityEditStatus>('loading');
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>(
    undefined
  );
  const [options, setOptions] = React.useState<readonly DictionaryOption[]>([]);

  const canEdit = data.permission === 'allowed' && slotMode === 'coding';
  const canSave = Boolean(
    canEdit && api?.updateBillingLocation && data.caseRunUid && data.serviceLine
  );

  const enterEdit = React.useCallback(() => {
    if (!canEdit) return;
    setMode('edit');
    setIsOpen(true);
  }, [canEdit]);

  const editSession = React.useRef(0);
  const committedLocation = React.useRef<string | undefined>(undefined);

  const resetEditSession = React.useCallback(() => {
    editSession.current += 1;
    setMode('view');
    setStatus('loading');
    setErrorMessage(undefined);
    setOptions([]);
  }, []);

  const leaveEdit = React.useCallback(() => {
    resetEditSession();
    triggerRef.current?.focus();
  }, [resetEditSession]);

  // Chakra's focus hooks are inert here: `useFocusOnShow` runs in a `useUpdateEffect`
  // that skips mount, and the panel is already mounted. Focus is driven from this
  // component instead. Entry is UNCONDITIONAL: on the keyboard and click paths
  // focus is still on the trigger, which a guard would mistake for someone else
  // owning it.
  React.useEffect(() => {
    if (mode !== 'edit') return undefined;
    const id = requestAnimationFrame(() => {
      if (selectRef.current && !selectRef.current.disabled) {
        selectRef.current.focus();
      } else {
        popoverRef.current?.focus();
      }
    });
    return () => cancelAnimationFrame(id);
  }, [mode]);

  // The select is disabled until the dictionary lands, and a case refetch swaps `api`
  // and sends it back to disabled -- so hand focus over whenever it becomes usable,
  // but only from where this component parked it. `data.location` is a dependency
  // because it keys the form below: our own save coming back keeps the editor open
  // but remounts it, destroying the focused select and leaving `activeElement` on
  // `<body>` without `mode` or `status` moving.
  React.useEffect(() => {
    if (mode !== 'edit' || status !== 'ready') return;
    const active = document.activeElement;
    if (active !== document.body && active !== popoverRef.current) return;
    selectRef.current?.focus();
  }, [mode, status, data.location]);

  // Advisory while editing: the hover branch schedules unguarded close timers
  // that survive the trigger flip.
  const handleClose = React.useCallback(() => {
    if (mode === 'edit') return;
    setIsOpen(false);
  }, [mode]);

  React.useEffect(() => {
    if (!isOpen || status === 'saving') return undefined;

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setIsOpen(false);
      resetEditSession();
    };

    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [isOpen, status, resetEditSession]);

  React.useEffect(() => {
    if (mode !== 'edit') return undefined;

    const load = api?.loadDictionaryOptions;
    if (!load) {
      setOptions([]);
      setStatus('unavailable');
      setErrorMessage(LOAD_FAILED);
      return undefined;
    }

    let abandoned = false;
    setStatus('loading');
    setErrorMessage(undefined);
    load(FACILITY_DICTIONARY)
      .then((loaded) => {
        if (abandoned) return;
        setOptions(loaded);
        setStatus(loaded.length > 0 ? 'ready' : 'unavailable');
        setErrorMessage(loaded.length > 0 ? undefined : NO_OPTIONS);
      })
      .catch(() => {
        if (abandoned) return;
        setOptions([]);
        setStatus('unavailable');
        setErrorMessage(LOAD_FAILED);
      });

    return () => {
      abandoned = true;
    };
  }, [mode, api]);

  React.useEffect(() => {
    if (mode === 'edit' && !canEdit) leaveEdit();
  }, [mode, canEdit, leaveEdit]);

  const caseRunUid = React.useRef(data.caseRunUid);
  React.useEffect(() => {
    if (caseRunUid.current === data.caseRunUid) return;
    caseRunUid.current = data.caseRunUid;
    if (mode === 'edit') leaveEdit();
  }, [data.caseRunUid, mode, leaveEdit]);

  const editedLocation = React.useRef(data.location);
  React.useEffect(() => {
    if (editedLocation.current === data.location) return;
    // Defer while a save is in flight -- advancing the ref here would swallow the
    // change instead of acting on it once the write settles.
    if (status === 'saving') return;
    const wasOurs = committedLocation.current === data.location;
    editedLocation.current = data.location;
    committedLocation.current = undefined;
    if (mode === 'edit' && !wasOurs) leaveEdit();
  }, [data.location, mode, status, leaveEdit]);

  const onSave = async (billingLocation: string) => {
    if (!canSave || !api?.updateBillingLocation || !data.caseRunUid) {
      return;
    }
    const session = editSession.current;
    setStatus('saving');
    setErrorMessage(undefined);
    try {
      await api.updateBillingLocation({
        caseRunUid: data.caseRunUid,
        serviceLine: data.serviceLine,
        billingLocation
      });
    } catch {
      // The coder can abandon, or the case change, while the PUT is outstanding;
      // this settlement then belongs to a session that no longer exists.
      if (editSession.current !== session) return;
      setStatus('ready');
      setErrorMessage(SAVE_FAILED);
      return;
    }
    if (editSession.current !== session) return;
    committedLocation.current = billingLocation;
    leaveEdit();
  };

  return (
    <Popover
      isOpen={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={handleClose}
      trigger={mode === 'edit' ? 'click' : 'hover'}
      closeOnBlur={mode !== 'edit'}
      closeOnEsc={mode !== 'edit'}
      placement="bottom-start"
    >
      <PopoverTrigger>
        <Text
          as="span"
          ref={triggerRef}
          tabIndex={0}
          onClick={mode === 'edit' ? undefined : enterEdit}
          onKeyDown={(event) => {
            if (!canEdit) return;
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              enterEdit();
            }
          }}
          aria-label={`Facility: ${na(data.location)}`}
          data-testid="billing-location"
          fontFamily={INTER_STACK}
          fontSize="12px"
          fontWeight={500}
          color={INK}
          sx={HOVER_HINT_SX}
        >
          ⌂ {na(data.location)}
        </Text>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          ref={popoverRef}
          onKeyDown={(event) => {
            if (mode !== 'edit') return;
            if (event.key === 'Escape') {
              event.stopPropagation();
              leaveEdit();
            }
          }}
          data-testid="billing-location-popover"
          data-edit-permission={data.permission ?? 'pending'}
          bg={SURFACE_WARM}
          border={`1px solid ${POPOVER_BORDER}`}
          borderRadius={POPOVER_RADIUS}
          boxShadow={POPOVER_SHADOW}
          padding="11px 13px"
          width="max-content"
          minWidth="180px"
          maxWidth="280px"
          // Positioning context for the edit affordance, which is inset from these edges.
          position="relative"
        >
          {mode === 'edit' ? (
            <FacilityEditForm
              key={String(data.location ?? '')}
              currentValue={
                typeof data.location === 'string' ? data.location : ''
              }
              department={na(data.department)}
              options={options}
              status={status}
              errorMessage={errorMessage}
              canSave={canSave}
              selectRef={selectRef}
              onSave={onSave}
              onCancel={leaveEdit}
            />
          ) : (
            <>
              <Text
                as="p"
                data-testid="billing-location-name"
                fontFamily={INTER_STACK}
                fontSize="12.5px"
                fontWeight={700}
                color={INK}
                paddingRight={canEdit ? EDIT_AFFORDANCE_GUTTER : undefined}
              >
                {na(data.location)}
              </Text>
              <Text
                as="p"
                data-testid="billing-location-department"
                fontFamily={INTER_STACK}
                fontSize="11px"
                fontWeight={400}
                color={INK_MUTED}
                marginTop="2px"
              >
                {na(data.department)}
              </Text>
              {canEdit && (
                <IconButton
                  data-testid="facility-edit-button"
                  // The icon carries no text, so this is the only thing naming the control.
                  aria-label="Edit facility"
                  icon={
                    // Drawn inline rather than imported: project_starburst's isolation boundary
                    // allows `@chakra-ui/react` but not `@chakra-ui/icons`, and the sanctioned
                    // CMXIcon set carries no edit glyph.
                    <Icon
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeWidth={2}
                    >
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </Icon>
                  }
                  size="xs"
                  variant="ghost"
                  color={INK_MUTED}
                  minWidth="auto"
                  height="auto"
                  padding="2px"
                  position="absolute"
                  // One constant for both edges: two literals drift apart on the first tweak.
                  top={EDIT_AFFORDANCE_INSET}
                  right={EDIT_AFFORDANCE_INSET}
                  onClick={enterEdit}
                />
              )}
            </>
          )}
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
